import { useCallback, useLayoutEffect, useState } from "react";

/** Sticky offset in pixels, keyed by column id. */
export type PinnedOffsets = Map<string, number>;

/**
 * Cumulative sticky offsets for pinned columns, measured from the rendered
 * header. TanStack's `getStart`/`getAfter` sum *declared* sizes, but a `<th>`
 * grows past its declared width when the content doesn't fit — as a translated
 * label does ("Edit" 50px, "Bearbeiten" 72px) — which slides a pinned column
 * under its opaque neighbour and clips it.
 *
 * `columnSignature` must change with the visible column set so the observer
 * picks up the new cells. Empty until the first measurement lands, and for
 * tables with nothing pinned; callers fall back to the declared offsets.
 */
export function usePinnedOffsets(
  tableRef: React.RefObject<HTMLElement | null>,
  columnSignature: string,
): PinnedOffsets {
  const [offsets, setOffsets] = useState<PinnedOffsets>(() => new Map());

  const measure = useCallback(() => {
    const table = tableRef.current;
    if (!table) return;

    const cells = pinnedHeaderCells(table);
    if (cells.length === 0) return;

    const next: PinnedOffsets = new Map();

    // Left pins accumulate from the leading edge, right pins from the trailing
    // one — hence the reverse walk below.
    let leftOffset = 0;
    for (const cell of cells) {
      const key = cell.dataset.stickyKey;
      if (key === undefined || !cell.hasAttribute("data-sticky")) continue;
      next.set(key, leftOffset);
      leftOffset += cell.getBoundingClientRect().width;
    }

    let rightOffset = 0;
    for (let i = cells.length - 1; i >= 0; i--) {
      const cell = cells[i];
      const key = cell.dataset.stickyKey;
      if (key === undefined || !cell.hasAttribute("data-sticky-right"))
        continue;
      next.set(key, rightOffset);
      rightOffset += cell.getBoundingClientRect().width;
    }

    setOffsets((prev) => (sameOffsets(prev, next) ? prev : next));
  }, [tableRef]);

  useLayoutEffect(() => {
    const table = tableRef.current;
    if (!table) return;

    // Most tables pin nothing — nothing to watch.
    const cells = pinnedHeaderCells(table);
    if (cells.length === 0) return;

    // Measure before paint; the observer's own first delivery only lands as a
    // state update on a later commit, so without this the first painted frame
    // uses the declared-size fallback.
    measure();

    // These cells resize on locale switch, font loading and container resizes,
    // and the offsets depend on their widths alone.
    const observer = new ResizeObserver(measure);
    cells.forEach((cell) => observer.observe(cell));

    return () => observer.disconnect();
  }, [tableRef, measure, columnSignature]);

  return offsets;
}

/**
 * Pinned cells of the header's leaf row, in DOM order. The leaf row is the last
 * one — one cell per rendered column, so its widths *are* the column widths.
 * A grouped table adds a spanning row *above* this one, which is why only the
 * last row is read; a group header's offset is its outermost leaf's, which
 * `getStickyCellProps` looks up under that leaf's key.
 * Matching on attribute presence keeps this in step with `table.module.css`.
 */
function pinnedHeaderCells(table: HTMLElement): HTMLElement[] {
  const leafRow = table.querySelector("thead tr:last-child");
  if (!leafRow) return [];

  return Array.from(
    leafRow.querySelectorAll<HTMLElement>(
      "th[data-sticky-key][data-sticky], th[data-sticky-key][data-sticky-right]",
    ),
  );
}

function sameOffsets(a: PinnedOffsets, b: PinnedOffsets): boolean {
  if (a.size !== b.size) return false;
  for (const [key, value] of b) {
    const prev = a.get(key);
    // Sub-pixel jitter from fractional layout widths would otherwise loop.
    if (prev === undefined || Math.abs(prev - value) > 0.5) return false;
  }
  return true;
}
